import { Stack } from '@mui/material';
import { t } from 'i18next';
import CrowdStrikeIcon from '../../../assets/images/crowdstrike-icon.svg';
import TaniumIcon from '../../../assets/images/tanium-icon.svg';
import VenafiIcon from '../../../assets/images/venafi-icon.svg';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { GenericError } from '../../../common/components/GenericError';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { bind } from '../../../common/utils/decorators';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { Integrations } from '../../../integrations/models/Integrations';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { IntegrationListItem } from '../components/IntegrationListItem';
import { JiraAccordion } from '../components/JiraAccordion';
import { JiraAccordionDetails } from '../components/JiraAccordionDetails';
import ServiceNowIcon from '../../../assets/images/service-now-icon.svg';

type State = BaseState & {
  isInitialLoading: boolean;
  isLoading: boolean;
  integrations: Integrations | null;
  canUpdateVenafi: boolean;
  canUpdateTanium: boolean;
  canUpdateJira: boolean;
  canUpdateCrowdStrike: boolean;
  canUpdateServiceNow: boolean;
};

type Props = {};

export class IntegrationsController extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isInitialLoading: true,
      isLoading: true,
      integrations: null,
      canUpdateTanium: false,
      canUpdateVenafi: false,
      canUpdateJira: false,
      canUpdateServiceNow: false,
      canUpdateCrowdStrike: false,
    };
  }

  @bind
  async fetchData() {
    try {
      this.setState({ isLoading: true });
      const [integrations, canUpdateVenafi, canUpdateTanium, canUpdateJira, canUpdateServiceNow, canUpdateCrowdStrike] = await Promise.all([
        this.integrationsRepository.getIntegrations(),
        this.permissionsService.canUpdateVenafi(),
        this.permissionsService.canUpdateTanium(),
        this.permissionsService.canUpdateJira(),
        this.permissionsService.canUpdateServiceNow(),
        this.permissionsService.canUpdateCrowdStrike(),
      ]);
      this.setState({
        isInitialLoading: false,
        isLoading: false,
        integrations,
        canUpdateTanium,
        canUpdateVenafi,
        canUpdateJira,
        canUpdateServiceNow,
        canUpdateCrowdStrike,
      });
    } catch (e: unknown) {
      this.setState({ isInitialLoading: false, isLoading: false, integrations: null });
      this.handleError(e);
    }
  }

  async componentDidMount() {
    super.componentDidMount();
    this.fetchData();
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.integrationsRepository.cancelAllRequests();
  }

  render() {
    const { integrations, isInitialLoading, isLoading, canUpdateVenafi, canUpdateTanium, canUpdateJira, canUpdateServiceNow, canUpdateCrowdStrike } =
      this.state;
    const isJiraExportEnabled = Boolean(integrations && integrations.getJiraExport() !== null);
    const isJiraIssueCollectorEnabled = Boolean(integrations && integrations.getJiraIssueCollector() !== null);
    const isJiraEnabled = isJiraExportEnabled || isJiraIssueCollectorEnabled;
    const isVenafiEnabled = Boolean(integrations && integrations.getVenafi() !== null);
    const isTaniumEnabled = Boolean(integrations && integrations.getTanium() !== null);
    const isCrowdStrikeConfigured = Boolean(integrations && integrations.getCrowdStrike() !== null);
    const isServiceNowEnabled = Boolean(integrations && integrations.getServiceNow() !== null);
    const isError = this.state.integrations === null && !this.state.isInitialLoading;

    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.INTEGRATIONS)} subtitle={t('integrationList.subtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <ComponentLoader loading={isLoading}>
          {isError ? (
            <GenericError onClick={this.fetchData} />
          ) : (
            <Stack gap={2}>
              <JiraAccordion
                isLoading={isInitialLoading}
                isJiraEnabled={isJiraEnabled}
                isConfigForm={false}
                details={
                  <JiraAccordionDetails
                    canUpdate={canUpdateJira}
                    isJiraExportEnabled={isJiraExportEnabled}
                    isJiraIssueCollectorEnabled={isJiraIssueCollectorEnabled}
                  />
                }
              />
              <IntegrationListItem
                isLoading={isInitialLoading}
                isConfigured={isVenafiEnabled}
                title={t('integrationList.venafi.title')}
                subtitle={t('integrationList.venafi.description')}
                icon={<VenafiIcon aria-hidden width="31" height="25" />}
                integrationPath={PATHS.INTEGRATION_VENAFI}
                canUpdate={canUpdateVenafi}
              />
              {getFeatureFlag<boolean>(FeatureFlagsType.ENABLE_TANIUM_INTEGRATION) && (
                <IntegrationListItem
                  isLoading={isInitialLoading}
                  isConfigured={isTaniumEnabled}
                  title={t('integrationList.tanium.title')}
                  subtitle={t('integrationList.tanium.description')}
                  icon={<TaniumIcon aria-hidden width="32" height="32" />}
                  integrationPath={PATHS.INTEGRATION_TANIUM}
                  canUpdate={canUpdateTanium}
                />
              )}
              <IntegrationListItem
                isLoading={isInitialLoading}
                isConfigured={isCrowdStrikeConfigured}
                title={t('integrationList.crowdstrike.title')}
                subtitle={t('integrationList.crowdstrike.description')}
                icon={<CrowdStrikeIcon aria-hidden width="32" height="25" />}
                integrationPath={PATHS.INTEGRATION_CROWDSTRIKE}
                canUpdate={canUpdateCrowdStrike}
              />
              {getFeatureFlag<boolean>(FeatureFlagsType.ENABLE_SERVICE_NOW) && (
                <IntegrationListItem
                  isLoading={isInitialLoading}
                  isConfigured={isServiceNowEnabled}
                  title={t('integrationList.serviceNow.title')}
                  subtitle={t('integrationList.serviceNow.description')}
                  icon={<ServiceNowIcon aria-hidden width="32" height="32" />}
                  integrationPath={PATHS.INTEGRATION_SERVICE_NOW}
                  canUpdate={canUpdateServiceNow}
                />
              )}
            </Stack>
          )}
        </ComponentLoader>
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationsBreadcrumbs();
  }
}
